/** Related history rows shown when expanding a linked assignment. */

import { inferAssignmentTransactions } from "./callsPlus";

export interface HistoryRelatedTxn {
  id?: number;
  linked_transaction_id?: number;
  instrument_type?: string;
  action?: string;
  side?: string;
  ticker?: string;
  trade_date?: string;
  quantity?: number;
  avg_price?: number;
  strike?: number;
  exp_date?: string;
  close_reason?: string;
  option_trans_code?: string;
  option_symbol?: string;
  trade_timestamp?: string;
  account_id?: string | null;
  /** Stock leg paired with an option assignment; hidden on History display. */
  assignment_companion?: boolean;
  /** Synthetic stock leg inferred from an assignment option row (not stored in the ledger). */
  implied_stock_leg?: boolean;
  notes_preview?: string;
  notes?: Array<{ body?: string | null; kind?: string | null }>;
  extensions?: Record<string, string>;
}

export type HistoryInstrumentKind = "option" | "stock" | "cash" | "unknown";

export function isImpliedStockLeg(txn: HistoryRelatedTxn): boolean {
  return txn.implied_stock_leg === true || (Number(txn.id) < 0 && txn.instrument_type === "stock");
}

export function historyInstrumentKind(txn: HistoryRelatedTxn): HistoryInstrumentKind {
  if (isImpliedStockLeg(txn)) return "stock";
  const kind = (txn.instrument_type ?? "").toLowerCase();
  if (kind === "option" || kind === "stock" || kind === "cash") return kind;
  return "unknown";
}

export function matchesHistoryTypeFilter(
  txn: HistoryRelatedTxn,
  type: "option" | "stock" | "cash",
): boolean {
  return historyInstrumentKind(txn) === type;
}

/** Side filter applies to option rows only; stock rows always pass. */
export function matchesHistorySideFilter(
  txn: HistoryRelatedTxn,
  side: "put" | "call",
): boolean {
  if (historyInstrumentKind(txn) !== "option") return true;
  return (txn.side ?? "").toLowerCase() === side;
}

function txnContracts(txn: HistoryRelatedTxn): number {
  return Number(txn.quantity) || 0;
}

function isAssignedOptionTxn(txn: HistoryRelatedTxn): boolean {
  const code = (txn.option_trans_code ?? "").toUpperCase();
  return (
    txn.instrument_type === "option" &&
    (txn.close_reason === "assigned" || code === "OASGN" || code === "ASGN" || code === "ASGN?")
  );
}

function findMatchingStockLeg(
  optionTxn: HistoryRelatedTxn,
  stockAction: "buy" | "sell",
  shares: number,
  byId: Map<number, HistoryRelatedTxn>,
  all: HistoryRelatedTxn[],
): HistoryRelatedTxn | undefined {
  const optionId = txnId(optionTxn);
  const linkedId = Number(optionTxn.linked_transaction_id);
  if (Number.isFinite(linkedId) && linkedId > 0) {
    const linked = byId.get(linkedId);
    if (
      linked?.instrument_type === "stock" &&
      linked.action === stockAction &&
      linked.ticker === optionTxn.ticker
    ) {
      return linked;
    }
  }

  for (const other of all) {
    if (other.instrument_type !== "stock" || other.action !== stockAction) continue;
    if (Number(other.linked_transaction_id) === optionId) return other;
  }

  for (const other of all) {
    if (other.instrument_type !== "stock" || other.action !== stockAction) continue;
    if (other.ticker !== optionTxn.ticker || other.trade_date !== optionTxn.trade_date) continue;
    const qty = Number(other.quantity) || 0;
    if (qty >= shares) return other;
  }

  return undefined;
}

/**
 * Stock Forge often records assignment option legs without a paired stock row.
 * History "Stocks" filter should still show those implied buy/sell legs.
 */
export function expandImpliedStockLegs(transactions: HistoryRelatedTxn[]): HistoryRelatedTxn[] {
  const byId = new Map<number, HistoryRelatedTxn>();
  for (const txn of transactions) {
    const id = txnId(txn);
    if (id) byId.set(id, txn);
  }

  const implied: HistoryRelatedTxn[] = [];
  const seenOptionIds = new Set<number>();

  for (const txn of transactions) {
    if (txn.instrument_type !== "option") continue;
    if (txn.side !== "put" && txn.side !== "call") continue;
    if (!isAssignedOptionTxn(txn)) continue;

    const stockAction = txn.side === "put" ? "buy" : "sell";
    const shares = Math.round(txnContracts(txn) * 100);
    if (shares <= 0 || !txn.ticker || !txn.trade_date) continue;

    const optionId = txnId(txn);
    if (!optionId || seenOptionIds.has(optionId)) continue;
    if (findMatchingStockLeg(txn, stockAction, shares, byId, transactions)) continue;

    seenOptionIds.add(optionId);
    const strike = Number(txn.strike) || 0;
    const accountId = String(txn.account_id ?? "").trim();
    implied.push({
      id: -optionId,
      instrument_type: "stock",
      action: stockAction,
      ticker: txn.ticker,
      trade_date: txn.trade_date,
      quantity: shares,
      avg_price: strike > 0 ? strike : undefined,
      linked_transaction_id: optionId,
      implied_stock_leg: true,
      ...(accountId ? { account_id: accountId } : {}),
    });
  }

  return implied.length ? [...transactions, ...implied] : transactions;
}

/**
 * Display/settlement rank so ASGN (then EXP) precedes buy/sell for the same
 * contract/ticker on one day.
 */
export function historySettlementRank(txn: HistoryRelatedTxn): number {
  if (isAssignedOptionTxn(txn)) return 0;
  const code = (txn.option_trans_code ?? "").toUpperCase();
  if (
    txn.instrument_type === "option" &&
    (txn.close_reason === "expired" || code === "OEXP" || code === "EXP" || code === "EXP?")
  ) {
    return 1;
  }
  if (historyInstrumentKind(txn) === "option") return 2;
  return 3; // stock BUY/SELL (including assignment companions)
}

function sameHistoryContractGroup(a: HistoryRelatedTxn, b: HistoryRelatedTxn): boolean {
  const tickerA = (a.ticker ?? "").trim().toUpperCase();
  const tickerB = (b.ticker ?? "").trim().toUpperCase();
  if (!tickerA || tickerA !== tickerB) return false;

  const fpA = contractFingerprint(a);
  const fpB = contractFingerprint(b);
  if (fpA && fpB) return fpA === fpB;

  // Option ↔ stock assignment pair (or two stock legs) on the same ticker/day.
  return true;
}

/**
 * Shared History ordering. Date direction controls newest/oldest-first;
 * on the same date + ticker/contract, ASGN precedes buy/sell, then sells
 * before buys (lot replay), then id ascending.
 */
export function compareHistoryRowsForDisplay(
  a: HistoryRelatedTxn,
  b: HistoryRelatedTxn,
  dateDirection: "asc" | "desc" = "asc",
): number {
  const dateCmp = String(a.trade_date ?? "").localeCompare(String(b.trade_date ?? ""));
  if (dateCmp !== 0) return dateDirection === "asc" ? dateCmp : -dateCmp;

  if (sameHistoryContractGroup(a, b)) {
    const rankCmp = historySettlementRank(a) - historySettlementRank(b);
    if (rankCmp !== 0) return rankCmp;
  }

  const sellFirst =
    (a.action === "sell" ? 0 : 1) - (b.action === "sell" ? 0 : 1);
  if (sellFirst !== 0) return sellFirst;
  return (Number(a.id) || 0) - (Number(b.id) || 0);
}

export function readHistoryRowTimestamp(txn: HistoryRelatedTxn): string {
  const direct = String(txn.trade_timestamp ?? "").trim();
  if (direct) return direct;
  const ext = (txn as HistoryRelatedTxn & { extensions?: Record<string, string> }).extensions;
  const fromExt =
    String(ext?.trade_timestamp ?? "").trim() ||
    String(ext?.pnl_timestamp ?? "").trim() ||
    String(ext?.broker_filled_at ?? "").trim();
  if (fromExt) return fromExt;
  const tradeDate = String(txn.trade_date ?? "").slice(0, 10);
  return tradeDate ? `${tradeDate}T00:00:00.000Z` : "";
}

function assignmentStockShares(option: HistoryRelatedTxn): number {
  return Math.round(txnContracts(option) * 100);
}

function expectedStockActionForAssignment(option: HistoryRelatedTxn): "buy" | "sell" | null {
  if (option.side === "put") return "buy";
  if (option.side === "call") return "sell";
  return null;
}

function stockMatchesAssignmentOption(
  stock: HistoryRelatedTxn,
  option: HistoryRelatedTxn,
): boolean {
  const expectedAction = expectedStockActionForAssignment(option);
  if (!expectedAction || stock.action !== expectedAction) return false;
  if ((stock.ticker ?? "") !== (option.ticker ?? "")) return false;
  if ((stock.trade_date ?? "") !== (option.trade_date ?? "")) return false;
  const shares = assignmentStockShares(option);
  if (shares <= 0) return false;
  const stockQty = Number(stock.quantity) || 0;
  return Math.abs(stockQty - shares) < 0.001 || stockQty >= shares;
}

function findAssignedOptionForStock(
  stock: HistoryRelatedTxn,
  all: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn | null {
  let exact: HistoryRelatedTxn | null = null;
  let fuzzy: HistoryRelatedTxn | null = null;
  for (const other of all) {
    if (!isAssignedOptionTxn(other)) continue;
    if (!stockMatchesAssignmentOption(stock, other)) continue;
    const shares = assignmentStockShares(other);
    const stockQty = Number(stock.quantity) || 0;
    if (Math.abs(stockQty - shares) < 0.001) {
      exact = other;
      break;
    }
    if (!fuzzy) fuzzy = other;
  }
  return exact ?? fuzzy;
}

function resolveAssignmentPairOptionId(
  txn: HistoryRelatedTxn,
  byId: Map<number, HistoryRelatedTxn>,
  all: readonly HistoryRelatedTxn[],
): number | null {
  if (isAssignedOptionTxn(txn)) {
    const id = txnId(txn);
    return id > 0 ? id : null;
  }
  if (historyInstrumentKind(txn) !== "stock") return null;

  // Prefer ticker/qty/day match — API linked_transaction_id often points at open STO.
  const matched = findAssignedOptionForStock(txn, all);
  if (matched) {
    const id = txnId(matched);
    return id > 0 ? id : null;
  }

  const linked = findLinkedTxn(txn, byId, all);
  if (linked && isAssignedOptionTxn(linked) && stockMatchesAssignmentOption(txn, linked)) {
    const id = txnId(linked);
    return id > 0 ? id : null;
  }

  return null;
}

function resolveAssignmentPairAnchorTimestamp(
  optionId: number,
  byId: Map<number, HistoryRelatedTxn>,
  all: readonly HistoryRelatedTxn[],
): string {
  const option = byId.get(optionId);
  if (!option) return "";
  let anchor = readHistoryRowTimestamp(option);
  for (const row of all) {
    if (row.instrument_type !== "stock") continue;
    if (!stockMatchesAssignmentOption(row, option)) continue;
    const ts = readHistoryRowTimestamp(row);
    if (ts.localeCompare(anchor) > 0) anchor = ts;
  }
  return anchor;
}

function ownLedgerAssignmentGroupId(
  txn: HistoryRelatedTxn,
  byId: Map<number, HistoryRelatedTxn>,
  all: readonly HistoryRelatedTxn[],
): number {
  const pairId = resolveAssignmentPairOptionId(txn, byId, all);
  if (pairId) return pairId;
  return txnId(txn) || 0;
}

function ownLedgerPrimaryTimestamp(
  txn: HistoryRelatedTxn,
  byId: Map<number, HistoryRelatedTxn>,
  all: readonly HistoryRelatedTxn[],
): string {
  const pairOptionId = resolveAssignmentPairOptionId(txn, byId, all);
  if (pairOptionId) {
    return resolveAssignmentPairAnchorTimestamp(pairOptionId, byId, all);
  }
  return readHistoryRowTimestamp(txn);
}

/** Timestamp used when interleaving cash flows with trades on History(L). */
export function historyDisplaySortTimestamp(
  txn: HistoryRelatedTxn,
  ctx?: {
    byId?: Map<number, HistoryRelatedTxn>;
    all?: readonly HistoryRelatedTxn[];
  },
): string {
  const byId = ctx?.byId;
  const all = ctx?.all;
  if (byId && all) return ownLedgerPrimaryTimestamp(txn, byId, all);
  return readHistoryRowTimestamp(txn);
}

/** History(L): newest trade timestamp first; assignment stock immediately follows ASGN. */
export function compareOwnLedgerHistoryRowsForDisplay(
  a: HistoryRelatedTxn,
  b: HistoryRelatedTxn,
  dateDirection: "asc" | "desc" = "desc",
  ctx?: {
    byId?: Map<number, HistoryRelatedTxn>;
    all?: readonly HistoryRelatedTxn[];
  },
): number {
  const dateCmp = String(a.trade_date ?? "").localeCompare(String(b.trade_date ?? ""));
  if (dateCmp !== 0) return dateDirection === "asc" ? dateCmp : -dateCmp;

  const byId = ctx?.byId;
  const all = ctx?.all;
  const tsA = byId && all ? ownLedgerPrimaryTimestamp(a, byId, all) : readHistoryRowTimestamp(a);
  const tsB = byId && all ? ownLedgerPrimaryTimestamp(b, byId, all) : readHistoryRowTimestamp(b);
  const tsCmp = tsA.localeCompare(tsB);
  if (tsCmp !== 0) return dateDirection === "asc" ? tsCmp : -tsCmp;

  if (byId && all) {
    const groupA = ownLedgerAssignmentGroupId(a, byId, all);
    const groupB = ownLedgerAssignmentGroupId(b, byId, all);
    if (groupA !== groupB) return groupB - groupA;

    const pairA = resolveAssignmentPairOptionId(a, byId, all);
    const pairB = resolveAssignmentPairOptionId(b, byId, all);
    if (pairA && pairA === pairB) {
      // Stock leg directly above its ASGN option (newer/higher in the list).
      const rankCmp = historySettlementRank(b) - historySettlementRank(a);
      if (rankCmp !== 0) return rankCmp;
    }
  }

  return (Number(b.id) || 0) - (Number(a.id) || 0);
}

/** Sort own-ledger rows for History(L) display. */
export function sortOwnLedgerHistoryForDisplay(
  transactions: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn[] {
  const byId = new Map<number, HistoryRelatedTxn>();
  for (const txn of transactions) {
    const id = txnId(txn);
    if (id > 0) byId.set(id, txn);
  }
  return [...transactions].sort((a, b) =>
    compareOwnLedgerHistoryRowsForDisplay(a, b, "desc", { byId, all: transactions }),
  );
}

/**
 * History(L) pipeline: infer assignment companions, keep stock legs visible, sort
 * for display. Shows the ledger as imported — no stock legs are invented for
 * assignments that lack one, since the assignment row itself carries the avg cost,
 * P/L and share position. (Remote history still expands them; Stock Forge records
 * assignment option legs without a paired stock row.)
 */
export function prepareOwnLedgerHistoryTransactions(
  transactions: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn[] {
  const inferred = inferAssignmentTransactions([...transactions]) as HistoryRelatedTxn[];
  return sortOwnLedgerHistoryForDisplay(inferred);
}

/** Chronological order for lot replay; ASGN before buy/sell, sells before buys. */
export function sortHistoryTransactionsChronological(
  transactions: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn[] {
  return [...transactions].sort((a, b) => compareHistoryRowsForDisplay(a, b, "asc"));
}

interface StockSharePosition {
  qty: number;
  avg: number;
}

function stockLegPrice(txn: HistoryRelatedTxn): number {
  const price = Number(txn.avg_price);
  if (Number.isFinite(price) && price > 0) return price;
  const strike = Number(txn.strike);
  if (Number.isFinite(strike) && strike > 0) return strike;
  return 0;
}

function blendStockBuy(pos: StockSharePosition, qty: number, price: number): void {
  if (qty <= 0) return;
  const totalCost = pos.qty * pos.avg + qty * price;
  pos.qty += qty;
  pos.avg = pos.qty > 0 ? totalCost / pos.qty : 0;
}

function reduceStockQty(pos: StockSharePosition, qty: number): void {
  if (qty <= 0) return;
  pos.qty = Math.max(0, pos.qty - qty);
  if (pos.qty === 0) pos.avg = 0;
}

export interface StockLegAvgCostDetail {
  action: "buy" | "sell";
  fillPrice: number;
  /** Holding avg before this transaction (0 if no open lot). */
  beforeAvg: number;
  /** Holding avg after this transaction (buys: blended; sells: remaining avg). */
  afterAvg: number;
}

function fmtHoldingAvgUsd(value: number): string {
  return `$${value.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** `old → new` for buys; remaining/prior avg for sells. Empty when unknown. */
export function formatStockHoldingAvgChange(detail: StockLegAvgCostDetail): string {
  if (detail.action === "buy") {
    if (!(detail.afterAvg > 0)) return "";
    if (detail.beforeAvg > 0) {
      return `${fmtHoldingAvgUsd(detail.beforeAvg)} → ${fmtHoldingAvgUsd(detail.afterAvg)}`;
    }
    return fmtHoldingAvgUsd(detail.afterAvg);
  }
  return detail.beforeAvg > 0 ? fmtHoldingAvgUsd(detail.beforeAvg) : "";
}

/** Running average cost snapshots for stock legs, keyed by transaction id. */
export function buildStockLegAvgCostDetailIndex(
  transactions: readonly HistoryRelatedTxn[],
): Map<number, StockLegAvgCostDetail> {
  const index = new Map<number, StockLegAvgCostDetail>();
  const positions = new Map<string, StockSharePosition>();

  for (const txn of sortHistoryTransactionsChronological(transactions)) {
    if (txn.instrument_type !== "stock") continue;
    if (txn.action !== "buy" && txn.action !== "sell") continue;
    const ticker = (txn.ticker ?? "").trim();
    if (!ticker) continue;

    const qty = Number(txn.quantity) || 0;
    const price = stockLegPrice(txn);
    if (qty <= 0 || price <= 0) continue;

    const book = `${String(txn.account_id ?? "").trim() || "_"}|${ticker}`;
    const pos = positions.get(book) ?? { qty: 0, avg: 0 };
    const id = txnId(txn);
    const beforeAvg = pos.avg > 0 && pos.qty > 0 ? pos.avg : 0;

    if (txn.action === "sell") {
      reduceStockQty(pos, qty);
      if (id) {
        index.set(id, {
          action: "sell",
          fillPrice: price,
          beforeAvg,
          afterAvg: pos.avg > 0 && pos.qty > 0 ? pos.avg : 0,
        });
      }
    } else {
      blendStockBuy(pos, qty, price);
      if (id) {
        index.set(id, {
          action: "buy",
          fillPrice: price,
          beforeAvg,
          afterAvg: pos.avg > 0 ? pos.avg : 0,
        });
      }
    }
    positions.set(book, pos);
  }

  return index;
}

/** Running average cost for stock legs, keyed by transaction id.
 *  Sells: avg before the sale. Buys: avg after the purchase. */
export function buildStockLegAvgCostIndex(
  transactions: readonly HistoryRelatedTxn[],
): Map<number, number> {
  const index = new Map<number, number>();
  for (const [id, detail] of buildStockLegAvgCostDetailIndex(transactions)) {
    if (detail.action === "sell") {
      if (detail.beforeAvg > 0) index.set(id, detail.beforeAvg);
    } else if (detail.afterAvg > 0) {
      index.set(id, detail.afterAvg);
    }
  }
  return index;
}

export function isDuplicateAssignmentStockLeg(txn: HistoryRelatedTxn): boolean {
  if (historyInstrumentKind(txn) !== "stock") return false;
  if (txn.assignment_companion) return true;
  return isImpliedStockLeg(txn);
}

/** Hide stock buy/sell rows that duplicate a matching assignment option leg. */
export function filterHistoryDisplayTransactions(
  transactions: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn[] {
  return transactions.filter((txn) => !isDuplicateAssignmentStockLeg(txn));
}

/**
 * History tab pipeline: mark assignment companions, infer assignment stock legs, sort.
 * Share history and History both use this.
 */
export function prepareHistoryTransactions(
  transactions: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn[] {
  const inferred = inferAssignmentTransactions([...transactions]);
  return sortHistoryTransactionsChronological(expandImpliedStockLegs(inferred));
}

/** Prepared ledger rows for History tab display (assignment duplicates removed). */
export function prepareHistoryDisplayTransactions(
  transactions: readonly HistoryRelatedTxn[],
): HistoryRelatedTxn[] {
  return filterHistoryDisplayTransactions(prepareHistoryTransactions(transactions));
}

/** Stock BUY/SELL rows for one ticker (recorded + implied assignment legs). */
export function historyStockLegsForTicker(
  transactions: readonly HistoryRelatedTxn[],
  ticker: string,
  accountId?: string | null,
): HistoryRelatedTxn[] {
  const key = ticker.trim().toUpperCase();
  if (!key) return [];
  const account = String(accountId ?? "").trim();

  return prepareHistoryTransactions(transactions).filter((txn) => {
    if (!matchesHistoryTypeFilter(txn, "stock")) return false;
    if ((txn.ticker ?? "").trim().toUpperCase() !== key) return false;
    if (txn.action !== "buy" && txn.action !== "sell") return false;
    if (!account) return true;
    return (String(txn.account_id ?? "").trim() || "_") === account;
  });
}

/** All ledger rows for one ticker (options + stock, incl. implied legs), newest first. */
export function historyTransactionsForTicker(
  transactions: readonly HistoryRelatedTxn[],
  ticker: string,
): HistoryRelatedTxn[] {
  const key = ticker.trim().toUpperCase();
  if (!key) return [];

  return prepareHistoryTransactions(transactions)
    .filter((txn) => (txn.ticker ?? "").trim().toUpperCase() === key)
    .sort((a, b) => {
      const dateCmp = String(b.trade_date ?? "").localeCompare(String(a.trade_date ?? ""));
      if (dateCmp !== 0) return dateCmp;
      return (Number(b.id) || 0) - (Number(a.id) || 0);
    });
}

export type HistoryDetailKind = "linked" | "opening";

export interface HistoryDetailRow {
  txn: HistoryRelatedTxn;
  kind: HistoryDetailKind;
}

function txnId(txn: HistoryRelatedTxn): number {
  return Number(txn.id) || 0;
}

function findLinkedTxn(
  txn: HistoryRelatedTxn,
  byId: Map<number, HistoryRelatedTxn>,
  all: HistoryRelatedTxn[],
): HistoryRelatedTxn | null {
  const id = txnId(txn);
  const linkedId = Number(txn.linked_transaction_id);
  if (Number.isFinite(linkedId) && linkedId > 0) {
    const linked = byId.get(linkedId);
    if (linked) return linked;
  }
  for (const other of all) {
    const otherId = txnId(other);
    if (otherId && otherId !== id && Number(other.linked_transaction_id) === id) {
      return other;
    }
  }
  return null;
}

function contractFingerprint(txn: HistoryRelatedTxn): string | null {
  if (txn.instrument_type !== "option") return null;
  const ticker = (txn.ticker ?? "").trim().toUpperCase();
  const side = (txn.side ?? "").trim().toLowerCase();
  const strike = Number(txn.strike);
  const exp = (txn.exp_date ?? "").trim();
  if (!ticker || !side || !exp || !Number.isFinite(strike)) return null;
  return `fp:${ticker}|${side}|${strike}|${exp}`;
}

export function resolveOptionSymbol(
  txn: HistoryRelatedTxn,
  linked: HistoryRelatedTxn | null,
): string | null {
  const candidates = [txn, linked].filter(Boolean) as HistoryRelatedTxn[];
  for (const row of candidates) {
    const sym = row.option_symbol?.trim();
    if (sym) return sym.toUpperCase();
  }
  for (const row of candidates) {
    const fp = contractFingerprint(row);
    if (fp) return fp;
  }
  return null;
}

export function isOpeningOptionTxn(txn: HistoryRelatedTxn): boolean {
  if (txn.instrument_type !== "option") return false;
  const code = (txn.option_trans_code ?? "").toUpperCase();
  if (code === "STO") return true;
  if (
    code === "BTC" ||
    code === "BTO" ||
    code === "STC" ||
    code === "ASGN" ||
    code === "ASGN?" ||
    code === "OASGN" ||
    code === "EXP" ||
    code === "EXP?" ||
    code === "OEXP"
  ) {
    return false;
  }
  if (txn.close_reason) return false;
  return txn.action === "sell";
}

function matchesContract(txn: HistoryRelatedTxn, contractKey: string): boolean {
  const sym = txn.option_symbol?.trim().toUpperCase();
  if (sym && sym === contractKey) return true;
  return contractFingerprint(txn) === contractKey;
}

export function buildOptionSymbolIndex(
  transactions: HistoryRelatedTxn[],
): Map<string, HistoryRelatedTxn[]> {
  const index = new Map<string, HistoryRelatedTxn[]>();
  for (const txn of transactions) {
    if (txn.instrument_type !== "option") continue;
    const key = resolveOptionSymbol(txn, null);
    if (!key) continue;
    const bucket = index.get(key) ?? [];
    bucket.push(txn);
    index.set(key, bucket);
  }
  return index;
}

function findOpeningTxn(
  contractKey: string,
  parent: HistoryRelatedTxn,
  all: HistoryRelatedTxn[],
  exclude: Set<number>,
  byContract: Map<string, HistoryRelatedTxn[]>,
): HistoryRelatedTxn | null {
  const bucket = byContract.get(contractKey) ?? all.filter((txn) => matchesContract(txn, contractKey));
  const opens = bucket.filter((txn) => isOpeningOptionTxn(txn) && !exclude.has(txnId(txn)));
  if (!opens.length) return null;

  const parentDate = String(parent.trade_date ?? "");
  const beforeParent = opens.filter((txn) => String(txn.trade_date ?? "") <= parentDate);
  const pool = beforeParent.length ? beforeParent : opens;

  pool.sort((a, b) => {
    const dateCmp = String(a.trade_date ?? "").localeCompare(String(b.trade_date ?? ""));
    if (dateCmp !== 0) return dateCmp;
    return txnId(a) - txnId(b);
  });

  return beforeParent.length ? pool[pool.length - 1]! : pool[0]!;
}

export function findHistoryDetailRows(
  txn: HistoryRelatedTxn,
  byId: Map<number, HistoryRelatedTxn>,
  all: HistoryRelatedTxn[],
  byContract: Map<string, HistoryRelatedTxn[]>,
): HistoryDetailRow[] {
  const exclude = new Set<number>([txnId(txn)]);
  const rows: HistoryDetailRow[] = [];

  const linked = findLinkedTxn(txn, byId, all);
  if (linked) exclude.add(txnId(linked));

  const contractKey = resolveOptionSymbol(txn, linked);
  const opening = contractKey ? findOpeningTxn(contractKey, txn, all, exclude, byContract) : null;
  if (linked) rows.push({ txn: linked, kind: "linked" });
  if (opening) rows.push({ txn: opening, kind: "opening" });

  rows.sort((a, b) => {
    const dateCmp = String(b.txn.trade_date ?? "").localeCompare(String(a.txn.trade_date ?? ""));
    if (dateCmp !== 0) return dateCmp;
    if (a.kind !== b.kind) return a.kind === "linked" ? -1 : 1;
    return txnId(b.txn) - txnId(a.txn);
  });

  return rows;
}
